import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { Loader2, TrendingUp, Euro, ChevronRight, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { api } from '@/lib/api';
import type { VerbruikGroep, VerbruikGroepen, VerbruikRegel } from '@/lib/api-types';
import { haalAlleRegels, jaarEnMaand } from '@/lib/verbruik';

/**
 * Het financieel resultaat: maand → praktijk → gebruiker → type → losse regel.
 *
 * Dit scherm haalde de laatste 2000 regels op en bouwde de hele boom daaruit in de
 * browser. Zodra er meer verbruik was, vielen de oudere maanden weg en waren de
 * bedragen in de boom te laag -- terwijl de totalen erboven uit een andere bron
 * kwamen en wel klopten. Nu komt elk niveau van de server, over al het verbruik:
 *
 *   maanden          GET /admin/usage/breakdown?groupBy=month, bij het openen
 *   praktijk tot type  dezelfde route voor één maand, bij het openklappen
 *   losse regels     GET /admin/usage/entries met de cursor, bij het openklappen
 *
 * De totalen bovenaan komen uit hetzelfde antwoord als de maanden, dus de som van
 * de maanden is het totaal. De maand is die in Nederlandse tijd, zoals in het
 * verbruikslog.
 */

/** Regels per verzoek bij het openklappen van een type: het maximum van de API. */
const REGELPAGINA = 2000;

/** Wat er onder een maand of een type geladen is. */
type Geladen<T> = T | 'laden' | 'fout';

interface Node {
  key: string;
  label: string;
  purchase: number;
  selling: number;
  /** Ontbreekt bij een losse regel: daaronder is niets meer. */
  children?: Node[];
  /** Of de kinderen nog onderweg zijn, of niet geladen konden worden. */
  status?: 'laden' | 'fout';
  /** Haalt de kinderen op; alleen zolang ze er niet zijn. */
  laad?: () => void;
  sortTime?: number;
}

const USAGE_TYPE_LABELS: Record<string, string> = {
  speech_recording: 'Spraakopname',
  soep_transcription: 'SOEP Samenvatting',
  translation: 'Vertaling',
  icpc_suggestion: 'ICPC Suggestie',
  patient_summary: 'Patiëntsamenvatting (PDF)',
  acs_call: 'Telefoongesprek (ACS)',
};

function euro(n: number) {
  return `€${n.toFixed(2).replace('.', ',')}`;
}

function formatMonth(monthStr: string): string {
  const [year, month] = monthStr.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
}

function formatLogLabel(log: VerbruikRegel): string {
  const dt = new Date(log.createdAt).toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts: string[] = [dt];
  if (log.seconds != null && Number(log.seconds) > 0) parts.push(`${Number(log.seconds).toFixed(0)}s`);
  if (log.model) parts.push(String(log.model));
  return parts.join(' · ');
}

function ensure(nodes: Map<string, Node>, key: string, label: string): Node {
  let node = nodes.get(key);
  if (!node) {
    node = { key, label, purchase: 0, selling: 0, children: [] };
    nodes.set(key, node);
  }
  return node;
}

/** De sleutel van een id dat ook null kan zijn: de groep zonder praktijk of gebruiker. */
function idSleutel(id: string | null | undefined): string {
  return id ?? 'none';
}

export function FinancialResultTab() {
  const [maanden, setMaanden] = useState<VerbruikGroep[]>([]);
  const [totalen, setTotalen] = useState<VerbruikGroep | null>(null);
  /** Per maand de groepen per praktijk, gebruiker en type. */
  const [perMaand, setPerMaand] = useState<Record<string, Geladen<VerbruikGroep[]>>>({});
  /** Per type-knoop de losse regels. */
  const [regels, setRegels] = useState<Record<string, Geladen<VerbruikRegel[]>>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const antwoord = await api.get<VerbruikGroepen>('/admin/usage/breakdown', {
          query: { groupBy: 'month' },
        });
        setMaanden(antwoord.groups);
        setTotalen(antwoord.totals);
      } catch {
        setMaanden([]);
        setTotalen(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const laadMaand = useCallback((maand: string) => {
    setPerMaand((prev) => ({ ...prev, [maand]: 'laden' }));
    void (async () => {
      try {
        const antwoord = await api.get<VerbruikGroepen>('/admin/usage/breakdown', {
          query: { ...jaarEnMaand(maand), groupBy: 'practice,user,type' },
        });
        setPerMaand((prev) => ({ ...prev, [maand]: antwoord.groups }));
      } catch {
        setPerMaand((prev) => ({ ...prev, [maand]: 'fout' }));
      }
    })();
  }, []);

  /**
   * De losse regels van één type, voor één gebruiker in één maand.
   *
   * Met `none` voor een praktijk of gebruiker die er niet is, zoals bij de
   * patiënt-mobiel: dan komen precies de regels van die groep terug.
   */
  const laadRegels = useCallback((sleutel: string, maand: string, groep: VerbruikGroep) => {
    setRegels((prev) => ({ ...prev, [sleutel]: 'laden' }));
    void (async () => {
      try {
        const alle = await haalAlleRegels<VerbruikRegel>(
          '/admin/usage/entries',
          {
            ...jaarEnMaand(maand),
            practiceId: idSleutel(groep.practiceId),
            practiceUserId: idSleutel(groep.practiceUserId),
            usageType: groep.usageType,
          },
          REGELPAGINA,
        );
        setRegels((prev) => ({ ...prev, [sleutel]: alle }));
      } catch {
        setRegels((prev) => ({ ...prev, [sleutel]: 'fout' }));
      }
    })();
  }, []);

  const toggle = (node: Node) => {
    const open = !expanded[node.key];
    setExpanded((prev) => ({ ...prev, [node.key]: open }));
    if (open) node.laad?.();
  };

  const tree = useMemo(() => {
    /** Kinderen of een status, en een laadfunctie zolang er niets is. */
    function onder<T>(geladen: Geladen<T> | undefined, laad: () => void): Pick<Node, 'status' | 'laad'> {
      if (geladen === undefined) return { laad };
      if (geladen === 'laden') return { status: 'laden' };
      if (geladen === 'fout') return { status: 'fout', laad };
      return {};
    }

    return [...maanden]
      .sort((a, b) => (b.month ?? '').localeCompare(a.month ?? ''))
      .map((m): Node => {
        const maand = m.month!;
        const groepen = perMaand[maand];
        const praktijken = new Map<string, Node>();
        const gebruikers = new Map<string, Node>();

        if (Array.isArray(groepen)) {
          for (const g of groepen) {
            const pKey = `${maand}|${idSleutel(g.practiceId)}`;
            const practiceNode = ensure(praktijken, pKey, g.practiceName || 'Onbekende praktijk');
            const uKey = `${pKey}|${idSleutel(g.practiceUserId)}`;
            if (!gebruikers.has(uKey)) {
              practiceNode.children!.push(ensure(gebruikers, uKey, g.userName || 'Onbekende gebruiker'));
            }
            const userNode = gebruikers.get(uKey)!;

            const tKey = `${uKey}|${g.usageType ?? 'unknown'}`;
            const geladen = regels[tKey];
            const typeNode: Node = {
              key: tKey,
              label: USAGE_TYPE_LABELS[g.usageType ?? ''] || g.usageType || 'Onbekend type',
              purchase: g.purchase,
              selling: g.selling,
              children: Array.isArray(geladen)
                ? geladen.map((log) => ({
                    key: `${tKey}|${log.id}`,
                    label: formatLogLabel(log),
                    purchase: log.purchaseCost ?? 0,
                    selling: log.sellingCost ?? 0,
                    sortTime: new Date(log.createdAt).getTime(),
                  }))
                : [],
              ...onder(geladen, () => laadRegels(tKey, maand, g)),
            };
            userNode.children!.push(typeNode);

            for (const n of [practiceNode, userNode]) {
              n.purchase += g.purchase;
              n.selling += g.selling;
            }
          }
        }

        return {
          key: maand,
          label: formatMonth(maand),
          purchase: m.purchase,
          selling: m.selling,
          children: [...praktijken.values()],
          ...onder(groepen, () => laadMaand(maand)),
        };
      });
  }, [maanden, perMaand, regels, laadMaand, laadRegels]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const grandTotals = {
    purchase: totalen?.purchase ?? 0,
    selling: totalen?.selling ?? 0,
    profit: (totalen?.selling ?? 0) - (totalen?.purchase ?? 0),
  };

  const renderStatus = (node: Node, depth: number) => (
    <tr key={`${node.key}|status`} className="border-t">
      <td colSpan={5} className="p-3 text-muted-foreground" style={{ paddingLeft: `${12 + depth * 20}px` }}>
        {node.status === 'laden' ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Laden...
          </span>
        ) : (
          'Kon dit niet laden. Klap opnieuw open om het nog eens te proberen.'
        )}
      </td>
    </tr>
  );

  const renderRow = (node: Node, depth: number) => {
    const hasChildren = node.children !== undefined;
    const isOpen = !!expanded[node.key];
    const profit = node.selling - node.purchase;
    const margin = node.selling > 0 ? (profit / node.selling) * 100 : 0;
    const children = [...(node.children ?? [])].sort((a, b) => {
      // Detailniveau (individuele logs): nieuwste bovenaan, oudste onderaan
      if (a.sortTime != null && b.sortTime != null) return b.sortTime - a.sortTime;
      return b.selling - a.selling;
    });
    return (
      <Fragment key={node.key}>
        <tr
          className={`border-t hover:bg-muted/50 ${hasChildren ? 'cursor-pointer' : ''}`}
          onClick={hasChildren ? () => toggle(node) : undefined}
        >
          <td className="p-3" style={{ paddingLeft: `${12 + depth * 20}px` }}>
            <span className="inline-flex items-center gap-1.5">
              {hasChildren ? (
                isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <span className="inline-block w-3.5" />
              )}
              <span className={depth === 0 ? 'capitalize font-medium' : ''}>{node.label}</span>
            </span>
          </td>
          <td className="p-3 text-right text-muted-foreground">{euro(node.purchase)}</td>
          <td className="p-3 text-right">{euro(node.selling)}</td>
          <td className={`p-3 text-right font-medium ${profit >= 0 ? 'text-green-600' : 'text-destructive'}`}>
            {euro(profit)}
          </td>
          <td className="p-3 text-right text-muted-foreground">{margin.toFixed(0)}%</td>
        </tr>
        {isOpen && node.status && renderStatus(node, depth + 1)}
        {isOpen && children.map((child) => renderRow(child, depth + 1))}
      </Fragment>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Financieel Resultaat
        </CardTitle>
        <CardDescription>Overzicht van kosten, omzet en winst per maand</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Grand totals */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Euro className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Totale Kosten</span>
            </div>
            <p className="text-2xl font-bold">€{grandTotals.purchase.toFixed(2).replace('.', ',')}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Euro className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Totale Omzet</span>
            </div>
            <p className="text-2xl font-bold">€{grandTotals.selling.toFixed(2).replace('.', ',')}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Totale Winst</span>
            </div>
            <p className={`text-2xl font-bold ${grandTotals.profit >= 0 ? 'text-green-600' : 'text-destructive'}`}>
              €{grandTotals.profit.toFixed(2).replace('.', ',')}
            </p>
          </div>
        </div>

        {/* Monthly table with drill-down */}
        {tree.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nog geen financiële data beschikbaar.</p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <p className="px-3 py-2 text-xs text-muted-foreground bg-muted/40">
              Klik door van maand → praktijk → gebruiker → type verbruik → losse registratie.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-medium">Maand</th>
                  <th className="text-right p-3 font-medium">Kosten (inkoop)</th>
                  <th className="text-right p-3 font-medium">Omzet (verkoop)</th>
                  <th className="text-right p-3 font-medium">Winst</th>
                  <th className="text-right p-3 font-medium">Marge</th>
                </tr>
              </thead>
              <tbody>
                {tree.map((m) => renderRow(m, 0))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
